import { useApp } from "../hooks/useApp";
import { ActivePendants } from "./all/ActivePendants/ActivePendants";
import { Pendants } from "./all/Pendants/Pendants";
import { CartBar } from "./all/CartBar/CartBar";
import { Preview } from "./all/Preview/Preview";
import { DesignSummaryPopup } from "./all/DesignSummaryPopup/DesignSummaryPopup";
import { PendantsTabs } from "./all/PendantsTabs/PendantsTabs";
import { Header } from "./all/Header/Header";
import { ChainVariants } from "./all/ChainVariants/ChainVariants";
import classNames from "classnames";
import { ConfirmationPopup } from "./all/ConfirmationPopup/ConfirmationPopup";
import { MetalToggler } from "./all/MetalToggler/MetalToggler";
import { BuilderGallery } from "./all/BuilderGallery/BuilderGallery";
import { DispatchEstimate } from "./all/DispatchEstimate/DispatchEstimate";
import { BauCtas } from "./all/BauCtas/BauCtas";
import { ActiveProductVariantDescription } from "./all/ActiveProductVariantDescription/ActiveProductVariantDescription";
import { useEffect, useState } from "react";
import { getMainProductVariantById, getSinglePendantMainProductVariant } from "../utils";
import {
  BuilderProductDetails,
  type ProductDetailsItem,
} from "./all/BuilderProductDetails/BuilderProductDetails";

export const App = () => {
  const {
    mainProduct,
    activeScreen,
    activeMainProductVariantId,
    activePendantProductVariantIds,
    active,
    activePendantProductId,
    isMainProductSinglePendant,
  } = useApp()
  const showBauCtas = mainProduct.globalSettings.enableBauCtas
  const [productDetailsItems, setProductDetailsItems] = useState<ProductDetailsItem[]>([])
  const activePendantProduct = mainProduct.metafields.pendantProducts.find(({ id }) => id === activePendantProductId)
  const activePendantProductTitle = activePendantProduct?.title.toLowerCase() ?? ''
  const hideActiveVariantDescription = isMainProductSinglePendant && (
    activePendantProductTitle.includes('birthstone') || activePendantProductTitle.includes('letter')
  )
  const activeSinglePendantVariantId = activePendantProductVariantIds[active] ?? activePendantProductVariantIds[0]
  const activeMainProductVariant = isMainProductSinglePendant && activeSinglePendantVariantId
    ? getSinglePendantMainProductVariant(mainProduct, Number(activeMainProductVariantId), Number(activeSinglePendantVariantId))
    : getMainProductVariantById(mainProduct, activeMainProductVariantId)
  const activeDescriptionHtml = isMainProductSinglePendant
    ? activeMainProductVariant?.metafields.description || mainProduct.descriptionHtml
    : ''
  const activeDetailsHtml = isMainProductSinglePendant
    ? activeMainProductVariant?.metafields.details || mainProduct.descriptionHtml
    : ''

  useEffect(() => {
    const source = document.getElementById('builder-product-tabs-source')
    const target = document.getElementById('builder-product-tabs-target')

    if (!source) {
      setProductDetailsItems([])
      return
    }

    const contentByIndex = new Map(
      Array.from(source.querySelectorAll<HTMLElement>('.js-tab-switch-content')).map((item) => {
        return [item.dataset.index ?? '', item.innerHTML]
      })
    )

    if (isMainProductSinglePendant && activeDescriptionHtml) {
      contentByIndex.set('1', activeDescriptionHtml)
    }

    if (isMainProductSinglePendant && activeDetailsHtml) {
      contentByIndex.set('2', activeDetailsHtml)
    }

    const nextProductDetailsItems = Array.from(source.querySelectorAll<HTMLElement>('.js-tab-switch-title'))
      .map((item) => {
        const id = item.dataset.index ?? ''
        const content = contentByIndex.get(id)

        if (!id || !content) {
          return null
        }

        return {
          content,
          id,
          title: item.textContent?.trim() ?? '',
        }
      })
      .filter((item): item is ProductDetailsItem => item !== null)

    setProductDetailsItems(nextProductDetailsItems)

    if (!target) {
      return
    }

    target.innerHTML = source.innerHTML

    if (isMainProductSinglePendant && activeDescriptionHtml) {
      const descriptionContent = target.querySelector<HTMLElement>('.js-tab-switch-content[data-index="1"]')

      if (descriptionContent) {
        descriptionContent.innerHTML = activeDescriptionHtml
      }
    }

    if (isMainProductSinglePendant && activeDetailsHtml) {
      const detailsContent = target.querySelector<HTMLElement>('.js-tab-switch-content[data-index="2"]')

      if (detailsContent) {
        detailsContent.innerHTML = activeDetailsHtml
      }
    }

    return () => {
      target.innerHTML = ''
    }
  }, [activeDescriptionHtml, activeDetailsHtml, isMainProductSinglePendant, mainProduct.id])

  const chainSection = isMainProductSinglePendant ? (
    <>
      <MetalToggler />
      <ChainVariants />
    </>
  ) : (
    <>
      <ChainVariants />
      <MetalToggler className="mt-5" />
    </>
  )

  const pendantSection = (
    <>
      <ActivePendants className="mt-2" />
      {mainProduct.metafields.pendantProducts.length > 1 && <PendantsTabs className="mt-4 xl:mt-7" />}
      <Pendants className="mt-4" />
    </>
  )

  return (
    <>
      <section className="xl-max:relative xl:grid xl:grid-cols-2 xl:gap-5 xl:min-h-screen xl:p-5">
        <div className="rounded-sm xl-max:overflow-hidden">
          <Preview
            className={classNames(
              'builder-preview w-full h-auto aspect-square bg-[#f8f7f5] xl-max:transform  xl:sticky xl:top-20 xl:bg-[#f2f1ed] xl-max:scale-[1.2]',
              {
                'xl-max:translate-y-[10%]': mainProduct.metafields.type === 'Necklace',
                'xl-max:translate-y-[5%]': mainProduct.metafields.type === 'Bracelet'
              }
            )}
          />
        </div>

        <div className="builder-info px-5 xl:pt-[60px] xl:px-[120px]">
          <Header />

          <div className="relative mt-[45px] flex flex-col">
            {isMainProductSinglePendant ? (
              <>
                <div className="order-2 mt-5 xl:order-1 xl:mt-0">
                  {chainSection}
                </div>
                <div className="order-1 xl:order-2">
                  {pendantSection}
                </div>
              </>
            ) : (
              <>
                {activeScreen === 'CHAIN' && chainSection}
                {activeScreen === 'PENDANTS' && pendantSection}
              </>
            )}
          </div>

          {!hideActiveVariantDescription && <ActiveProductVariantDescription className="mt-5 xl:mt-10" />}

          <DispatchEstimate className="md:mt-5 pb-2 text-left" />

          {showBauCtas ? (
            <BauCtas />
          ) : (
            <CartBar className="fixed left-5 right-5 z-[1] bottom-5 xl:static xl:mt-8" />
          )}

          <BuilderProductDetails className="mt-8" items={productDetailsItems} />
          <div id="builder-product-tabs-target" className="mt-8 hidden xl:block" />
        </div>
      </section>

      <BuilderGallery />
      <DesignSummaryPopup/>
      <ConfirmationPopup />
    </>
  )
}
